import { type ReactElement } from 'react';
import { can, type DayMode, type FloorStatus, type Membership } from '@kratispot/shared';
import { useTranslation, type CopyKey } from '@/lib/i18n';

type Move = { readonly status: FloorStatus; readonly label: CopyKey };

// What a whole selection may be told to do at once; see §10 and §7.
const BULK_MOVES: readonly Move[] = [
  { status: 'occupied', label: 'bulkOccupied' },
  { status: 'free', label: 'bulkFree' },
  { status: 'cleaning', label: 'bulkCleaning' },
];

// Bulk actions for a whole selection; a future day plans, it does not move. §10
export function SelectBar({
  count,
  mode,
  busy,
  membership,
  anyOnline,
  payingNumbers,
  onStatus,
  onStaffOnly,
  onReserve,
  onClose,
}: {
  count: number;
  mode: DayMode;
  busy: boolean;
  membership: Membership;
  anyOnline: boolean;
  // Spots a checkout is paying for: the API refuses the lot, so offer nothing. §13
  payingNumbers: readonly number[];
  onStatus: (status: FloorStatus) => void;
  onStaffOnly: (staffOnly: boolean) => void;
  onReserve: () => void;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  // §4: a bar of buttons this member cannot press is a bar of refusals.
  const owner = can(membership, 'venue:settings');
  const moves = mode === 'live' && can(membership, 'spot:status') ? BULK_MOVES : [];
  const mayBook = can(membership, 'reservation:write');
  const idle = busy || count === 0;

  return (
    <div
      role="toolbar"
      aria-label={t('select')}
      className="fixed bottom-2 left-1/2 z-64 mb-safe flex max-w-[96vw] -translate-x-1/2 items-center gap-1.5 overflow-x-auto rounded-2xl bg-foreground px-2.5 py-1.5 text-[13px] text-background shadow-2xl sm:bottom-4 sm:gap-2 sm:px-3 sm:py-2"
    >
      <b className="shrink-0 px-1 whitespace-nowrap">
        {count} {t('selectedCount')}
      </b>
      {payingNumbers.length > 0 && (
        <span role="status" className="px-1 text-[12px] font-semibold text-amber-300">
          {t('payingSelected')} {payingNumbers.map((number) => `#${number}`).join(', ')}
        </span>
      )}
      {mayBook && payingNumbers.length === 0 && (
        <button
          type="button"
          disabled={idle}
          onClick={onReserve}
          className="shrink-0 rounded-xl bg-primary px-3 py-1.5 font-bold whitespace-nowrap text-primary-foreground disabled:opacity-50 sm:px-3.5 sm:py-2"
        >
          {t('reserve')}
        </button>
      )}
      {(payingNumbers.length > 0 ? [] : moves).map((move) => (
        <button
          key={move.status}
          type="button"
          disabled={idle}
          onClick={() => onStatus(move.status)}
          className="shrink-0 rounded-xl bg-background/15 px-3 py-1.5 font-bold whitespace-nowrap disabled:opacity-50 sm:px-3.5 sm:py-2"
        >
          {t(move.label)}
        </button>
      ))}
      {/* One control, reading the selection: hide them all, or hand them back. §11 */}
      {owner && payingNumbers.length === 0 && (
        <button
          type="button"
          disabled={idle}
          onClick={() => onStaffOnly(anyOnline)}
          className="shrink-0 rounded-xl bg-background/15 px-3 py-1.5 font-bold whitespace-nowrap disabled:opacity-50 sm:px-3.5 sm:py-2"
        >
          {t(anyOnline ? 'bulkStaffOnly' : 'bulkOnline')}
        </button>
      )}
      <button
        type="button"
        aria-label={t('clearSelection')}
        onClick={onClose}
        className="shrink-0 rounded-xl px-2 py-1.5 font-bold opacity-70"
      >
        ✕
      </button>
    </div>
  );
}
